<template>
  <div
    role="tablist"
    class="segmented-control"
    :aria-label="ariaLabel"
  >
    <button
      v-for="(option, index) in options"
      :key="option.value"
      type="button"
      role="tab"
      :aria-selected="modelValue === option.value"
      :aria-label="option.label"
      class="segmented-option"
      :class="{ 'segmented-option--active': modelValue === option.value }"
      @click="select(option.value)"
    >
      <span class="segmented-option-text">{{ option.label }}</span>
    </button>
    <span
      class="segmented-slider"
      :style="sliderStyle"
    />
  </div>
</template>

<script setup lang="ts">
export interface SegmentedOption<T extends string = string> {
  value: T
  label: string
}

interface Props<T extends string = string> {
  modelValue: T
  options: SegmentedOption<T>[]
  ariaLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  ariaLabel: undefined
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const select = (value: string) => {
  emit('update:modelValue', value)
}

const activeIndex = computed(() => {
  const idx = props.options.findIndex(o => o.value === props.modelValue)
  return idx >= 0 ? idx : 0
})

const sliderStyle = computed(() => {
  const count = props.options.length
  const widthPercent = 100 / count
  return {
    width: `calc(${widthPercent}% - var(--segmented-padding, 4px))`,
    transform: `translateX(${activeIndex.value * 100}%)`
  }
})
</script>

<style scoped>
.segmented-control {
  --segmented-bg: var(--color-bg-faq);
  --segmented-active: #fff;
  --segmented-text: #3A3A3C;
  --segmented-text-active: #000;
  --segmented-radius: 8px;
  --segmented-padding: 4px;

  position: relative;
  display: inline-flex;
  background: var(--segmented-bg);
  border-radius: var(--segmented-radius);
  padding: var(--segmented-padding);
  overflow: hidden;
  box-sizing: border-box;
}

.segmented-option {
  position: relative;
  z-index: 1;
  flex: 1;
  min-width: 0;
  padding: 10px 16px;
  font-family: var(--font-inter);
  font-size: 15px;
  font-weight: 500;
  color: var(--segmented-text);
  background: transparent;
  border: none;
  border-radius: calc(var(--segmented-radius) - 2px);
  cursor: pointer;
  transition: color 0.2s ease;
}

.segmented-option:hover {
  color: var(--segmented-text-active);
}

.segmented-option--active {
  color: var(--segmented-text-active);
}

.segmented-option-text {
  display: block;
  text-align: center;
  white-space: nowrap;
}

.segmented-slider {
  position: absolute;
  top: var(--segmented-padding);
  left: var(--segmented-padding);
  bottom: var(--segmented-padding);
  background: var(--segmented-active);
  border-radius: calc(var(--segmented-radius) - 2px);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  pointer-events: none;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;
}
</style>
